export interface TempUploadReplacement {
  from: string;
  to: string;
}

export interface TempUploadRetryResult {
  success: boolean;
  replacements: TempUploadReplacement[];
  /** Whether this call actually retried at least one previously failed upload. */
  retried: boolean;
}

const replaceUploadUris = (
  value: unknown,
  replacements: Map<string, string>,
  seen: WeakMap<object, unknown>,
): unknown => {
  if (typeof value === 'string') return replacements.get(value) ?? value;
  if (!value || typeof value !== 'object') return value;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
  if (seen.has(value)) return seen.get(value);

  if (Array.isArray(value)) {
    const next: unknown[] = [];
    seen.set(value, next);
    value.forEach((item) => {
      next.push(replaceUploadUris(item, replacements, seen));
    });
    return next;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;

  const next: Record<string, unknown> = {};
  seen.set(value, next);
  Object.entries(value).forEach(([key, item]) => {
    next[key] = replaceUploadUris(item, replacements, seen);
  });
  return next;
};

export const applyTempUploadReplacements = <T>(value: T, replacements: TempUploadReplacement[]): T => {
  if (replacements.length === 0) return value;
  const replacementMap = new Map(replacements.map(({ from, to }) => [from, to]));
  return replaceUploadUris(value, replacementMap, new WeakMap()) as T;
};

export type TempUploadRetryHandler = () => Promise<TempUploadRetryResult>;

const retryHandlers = new Set<TempUploadRetryHandler>();

export const registerTempUploadRetryHandler = (handler: TempUploadRetryHandler): (() => void) => {
  retryHandlers.add(handler);
  return () => retryHandlers.delete(handler);
};

export const retryPendingTempUploads = async (): Promise<TempUploadRetryResult> => {
  const handlers = Array.from(retryHandlers);
  if (handlers.length === 0) return { success: true, replacements: [], retried: false };
  const results = await Promise.all(handlers.map((handler) => handler()));
  return {
    success: results.every((result) => result.success),
    replacements: results.flatMap((result) => result.replacements),
    retried: results.some((result) => result.retried),
  };
};
